/**
 * useEmbedApi — postMessage bridge for iframe embed mode (Phase 2)
 *
 * Listens for incoming commands from the parent window and dispatches
 * them to the engine. Sends outbound events back to the parent.
 *
 * Security: validates message origin against config.market.embedAllowedOrigins.
 * If no allowlist is configured, all origins are accepted (dev convenience).
 *
 * Inbound commands (parent → iframe):
 *   { type: 'ims:setColor',    colorId: string }
 *   { type: 'ims:setRim',      rimId: string }
 *   { type: 'ims:setView',     viewId: string }
 *   { type: 'ims:setScene',    sceneId: string }
 *   { type: 'ims:setAutoRotate', enabled?: boolean, speedPreset?: 'slow'|'moderate'|'fast', speed?: number, resumeDelay?: number, pauseOnInteraction?: boolean, easeInDuration?: number, invertRotation?: boolean }
 *   { type: 'ims:toggleDoor',  open?: boolean, name?: string }
 *   { type: 'ims:playAnimation', name: string, direction?: 'forward'|'reverse', speed?: number }
 *   { type: 'ims:toggleLights' }
 *   { type: 'ims:reset' }
 *
 * Outbound events (iframe → parent):
 *   { type: 'ims:ready' }
 *   { type: 'ims:colorChanged',  colorId: string }
 *   { type: 'ims:sceneChanged',  sceneId: string }
 *   { type: 'ims:viewChanged',   viewId: string }
 *   { type: 'ims:autoRotateChanged', enabled?: boolean }
 *   { type: 'ims:doorChanged',   open: boolean, name?: string }
 *   { type: 'ims:animationPlayed', name: string, direction?: 'forward'|'reverse', speed?: number }
 *   { type: 'ims:error',         code: string, message?: string }
 *
 * @see docs/ims-brand-frontends/01-architecture.md
 */
import { useEffect, useRef } from 'react';
import { applyScene } from '@ims/engine';
import type { Engine } from '@ims/engine';
import type { ResolvedConfig } from '@ims/shared';
import { useAppStore } from '../store/app.store';

/** Built-in transparent scene — engine preset only, no model.json entry needed. */
const TRANSPARENT_SCENE_ID = 'transparent';

interface UseEmbedApiOptions {
  engineRef: React.RefObject<Engine | null>;
  config: ResolvedConfig | null;
  sceneReady: boolean;
}

/**
 * Post a typed message to the parent window.
 * When a targetOrigin is provided, the message is scoped to that origin only.
 * Falls back to '*' when no allowlist is configured (dev convenience).
 */
function postToParent(data: Record<string, unknown>, targetOrigin?: string) {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(data, targetOrigin ?? '*');
  }
}

export function useEmbedApi({ engineRef, config, sceneReady }: UseEmbedApiOptions): void {
  const embedMode = useAppStore((s) => s.embedMode);
  const doorsOpen = useAppStore((s) => s.doorsOpen);
  const setSelectedColor = useAppStore((s) => s.setSelectedColor);
  const setSelectedRim = useAppStore((s) => s.setSelectedRim);
  const setActiveScene = useAppStore((s) => s.setActiveScene);
  const setDoorsOpen = useAppStore((s) => s.setDoorsOpen);
  const setTransparentBg = useAppStore((s) => s.setTransparentBg);
  const readySentRef = useRef(false);

  // ── Send ims:ready once scene is loaded ────────────────────────────────
  useEffect(() => {
    if (!embedMode || !sceneReady || readySentRef.current) return;
    readySentRef.current = true;
    // When an allowlist is configured, broadcast ready to each allowed origin.
    // Otherwise fall back to '*' (dev convenience).
    const origins = config?.market.embedAllowedOrigins;
    if (origins && origins.length > 0) {
      for (const origin of origins) {
        postToParent({ type: 'ims:ready' }, origin);
      }
    } else {
      postToParent({ type: 'ims:ready' });
    }
  }, [embedMode, sceneReady, config]);

  // ── Listen for inbound postMessage commands ────────────────────────────
  useEffect(() => {
    if (!embedMode) return;

    const allowedOrigins = config?.market.embedAllowedOrigins;

    function handleMessage(event: MessageEvent) {
      // Origin validation — if allowlist exists, enforce it
      if (allowedOrigins && allowedOrigins.length > 0) {
        if (!allowedOrigins.includes(event.origin)) {
          console.warn(`[IMS Embed] Blocked message from unauthorized origin: ${event.origin}`);
          return;
        }
      }

      // Scope outbound responses to the sender's origin when allowlist is active
      const replyOrigin = allowedOrigins?.length ? event.origin : undefined;

      const msg = event.data;
      if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') return;
      if (!msg.type.startsWith('ims:')) return;

      const engine = engineRef.current;
      if (!engine || !config) {
        postToParent(
          { type: 'ims:error', code: 'NOT_READY', message: 'Engine not initialized' },
          replyOrigin,
        );
        return;
      }

      try {
        switch (msg.type) {
          // ── Set exterior color ──────────────────────────────────────
          case 'ims:setColor': {
            const colorDef = config.colors.find((c) => c.id === msg.colorId);
            if (!colorDef) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'INVALID_COLOR',
                  message: `Color "${msg.colorId}" not found`,
                },
                replyOrigin,
              );
              return;
            }
            colorDef.primary.materials.forEach((mat) => {
              engine.setMaterial(mat, {
                preset: 'car-paint',
                colorCode: colorDef.primary.colorCode,
                type: colorDef.primary.type,
                animated: true,
              });
            });
            if (colorDef.secondary) {
              colorDef.secondary.materials.forEach((mat) => {
                engine.setMaterial(mat, {
                  preset: 'car-paint',
                  colorCode: colorDef.secondary!.colorCode,
                  type: colorDef.secondary!.type,
                  animated: true,
                });
              });
            }
            setSelectedColor(colorDef.id);
            postToParent({ type: 'ims:colorChanged', colorId: colorDef.id }, replyOrigin);
            break;
          }

          // ── Set rim ────────────────────────────────────────────────
          case 'ims:setRim': {
            const rimSet = config.rims.find((r) => r.id === msg.rimId);
            if (!rimSet) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'INVALID_RIM',
                  message: `Rim "${msg.rimId}" not found`,
                },
                replyOrigin,
              );
              return;
            }
            engine.showAsset(rimSet.id, { animated: false });
            setSelectedRim(rimSet.id);
            postToParent({ type: 'ims:rimChanged', rimId: rimSet.id }, replyOrigin);
            break;
          }

          // ── Set camera view ────────────────────────────────────────
          case 'ims:setView': {
            const views = config.model.camera.namedViews;
            const view = views?.[msg.viewId];
            if (!view) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'INVALID_VIEW',
                  message: `View "${msg.viewId}" not found`,
                },
                replyOrigin,
              );
              return;
            }
            engine.setViewpoint({
              position: view.position,
              target: view.target,
              transitionDuration: view.transitionDuration ?? 800,
            });
            postToParent({ type: 'ims:viewChanged', viewId: msg.viewId }, replyOrigin);
            break;
          }

          // ── Set scene / environment ────────────────────────────────
          case 'ims:setScene': {
            // 'transparent' is a built-in engine preset — no model.json entry required.
            if (msg.sceneId === TRANSPARENT_SCENE_ID) {
              engine.setPreset('transparent');
              setActiveScene(TRANSPARENT_SCENE_ID);
              setTransparentBg(true);
              postToParent(
                { type: 'ims:sceneChanged', sceneId: TRANSPARENT_SCENE_ID },
                replyOrigin,
              );
              return;
            }
            const scene = config.model.scenes?.[msg.sceneId];
            if (!scene) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'INVALID_SCENE',
                  message: `Scene "${msg.sceneId}" not found`,
                },
                replyOrigin,
              );
              return;
            }
            applyScene(engine, scene);
            setActiveScene(msg.sceneId);
            setTransparentBg(false);
            postToParent({ type: 'ims:sceneChanged', sceneId: msg.sceneId }, replyOrigin);
            break;
          }

          // ── Configure auto-rotate ───────────────────────────────────
          case 'ims:setAutoRotate': {
            const autoRotateCfg: Record<string, unknown> = {};

            if (typeof msg.enabled === 'boolean') {
              autoRotateCfg.enabled = msg.enabled;
            }
            if (
              typeof msg.speedPreset === 'string' &&
              (msg.speedPreset === 'slow' ||
                msg.speedPreset === 'moderate' ||
                msg.speedPreset === 'fast')
            ) {
              autoRotateCfg.speedPreset = msg.speedPreset;
            }
            if (typeof msg.speed === 'number' && Number.isFinite(msg.speed)) {
              autoRotateCfg.speed = msg.speed;
            }
            if (typeof msg.resumeDelay === 'number' && Number.isFinite(msg.resumeDelay)) {
              autoRotateCfg.resumeDelay = msg.resumeDelay;
            }
            if (typeof msg.pauseOnInteraction === 'boolean') {
              autoRotateCfg.pauseOnInteraction = msg.pauseOnInteraction;
            }
            if (typeof msg.easeInDuration === 'number' && Number.isFinite(msg.easeInDuration)) {
              autoRotateCfg.easeInDuration = msg.easeInDuration;
            }
            if (typeof msg.invertRotation === 'boolean') {
              autoRotateCfg.invertRotation = msg.invertRotation;
            }

            if (Object.keys(autoRotateCfg).length === 0) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'INVALID_PAYLOAD',
                  message:
                    'ims:setAutoRotate requires at least one valid field (enabled, speedPreset, speed, resumeDelay, pauseOnInteraction, easeInDuration, invertRotation)',
                },
                replyOrigin,
              );
              return;
            }

            engine.setAutoRotate(autoRotateCfg);
            postToParent(
              {
                type: 'ims:autoRotateChanged',
                ...(typeof autoRotateCfg.enabled === 'boolean'
                  ? { enabled: autoRotateCfg.enabled }
                  : {}),
              },
              replyOrigin,
            );
            break;
          }

          // ── Open/close doors + trunk animations ────────────────────
          case 'ims:toggleDoor': {
            if (!config.model.features.doorAnimation || !config.model.animations?.length) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'FEATURE_DISABLED',
                  message: 'Door animation not available for this model',
                },
                replyOrigin,
              );
              return;
            }

            const nextOpen = typeof msg.open === 'boolean' ? msg.open : !doorsOpen;
            const direction = nextOpen ? 'forward' : 'reverse';
            const clipName = typeof msg.name === 'string' ? msg.name.trim() : '';

            if (clipName) {
              const exists =
                clipName === '*' ||
                config.model.animations.some((anim) => anim.clipName === clipName);
              if (!exists) {
                postToParent(
                  {
                    type: 'ims:error',
                    code: 'INVALID_ANIMATION',
                    message: `Animation "${clipName}" not found`,
                  },
                  replyOrigin,
                );
                return;
              }
              engine.playAnimation(clipName, { direction });
            } else {
              // Default behavior: no name provided => apply to all configured door/trunk clips.
              for (const anim of config.model.animations) {
                engine.playAnimation(anim.clipName, { direction });
              }
            }

            setDoorsOpen(nextOpen);
            postToParent(
              {
                type: 'ims:doorChanged',
                open: nextOpen,
                ...(clipName ? { name: clipName } : {}),
              },
              replyOrigin,
            );
            break;
          }

          // ── Play specific animation (API parity with engine.playAnimation) ──
          case 'ims:playAnimation': {
            if (!config.model.features.doorAnimation || !config.model.animations?.length) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'FEATURE_DISABLED',
                  message: 'Animation playback not available for this model',
                },
                replyOrigin,
              );
              return;
            }

            if (typeof msg.name !== 'string' || msg.name.trim().length === 0) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'INVALID_PAYLOAD',
                  message: 'ims:playAnimation requires a non-empty "name"',
                },
                replyOrigin,
              );
              return;
            }

            const name = msg.name.trim();
            const exists =
              name === '*' || config.model.animations.some((anim) => anim.clipName === name);
            if (!exists) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'INVALID_ANIMATION',
                  message: `Animation "${name}" not found`,
                },
                replyOrigin,
              );
              return;
            }

            const options: Record<string, unknown> = {};
            if (msg.direction === 'forward' || msg.direction === 'reverse') {
              options.direction = msg.direction;
            }
            if (typeof msg.speed === 'number' && Number.isFinite(msg.speed)) {
              options.speed = msg.speed;
            }

            engine.playAnimation(name, options);
            postToParent(
              {
                type: 'ims:animationPlayed',
                name,
                ...(options.direction ? { direction: options.direction } : {}),
                ...(typeof options.speed === 'number' ? { speed: options.speed } : {}),
              },
              replyOrigin,
            );
            break;
          }

          // ── Toggle light signature ─────────────────────────────────
          case 'ims:toggleLights': {
            if (!config.model.features.lightSignature) {
              postToParent(
                {
                  type: 'ims:error',
                  code: 'FEATURE_DISABLED',
                  message: 'Light signature not available for this model',
                },
                replyOrigin,
              );
              return;
            }
            const state = engine.getLightSignatureState();
            const next = !state.isOn;
            engine.setLightSignature(next, true);
            postToParent({ type: 'ims:lightsChanged', on: next }, replyOrigin);
            break;
          }

          // ── Reset to initial state ─────────────────────────────────
          case 'ims:reset': {
            engine.resetCamera();
            // Re-apply default scene
            const defaultSceneId = config.model.defaultScene ?? '';
            const defaultScene = config.model.scenes?.[defaultSceneId];
            if (defaultScene) {
              applyScene(engine, defaultScene);
              setActiveScene(defaultSceneId);
            }
            setTransparentBg(false);
            // Re-apply first color
            if (config.colors.length > 0) {
              const first = config.colors[0];
              first.primary.materials.forEach((mat) => {
                engine.setMaterial(mat, {
                  preset: 'car-paint',
                  colorCode: first.primary.colorCode,
                  type: first.primary.type,
                  animated: false,
                });
              });
              if (first.secondary) {
                first.secondary.materials.forEach((mat) => {
                  engine.setMaterial(mat, {
                    preset: 'car-paint',
                    colorCode: first.secondary!.colorCode,
                    type: first.secondary!.type,
                    animated: false,
                  });
                });
              }
              setSelectedColor(first.id);
            }
            // Re-apply default rim
            if (config.model.rims?.defaultId) {
              engine.showAsset(config.model.rims.defaultId, { animated: false });
              setSelectedRim(config.model.rims.defaultId);
            }
            postToParent({ type: 'ims:reset' }, replyOrigin);
            break;
          }

          default:
            // Unknown command — ignore silently
            break;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        console.error('[IMS Embed] Command failed:', msg.type, err);
        postToParent({ type: 'ims:error', code: 'COMMAND_FAILED', message }, replyOrigin);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    embedMode,
    config,
    doorsOpen,
    engineRef,
    setSelectedColor,
    setSelectedRim,
    setActiveScene,
    setDoorsOpen,
  ]);
}
